import useSizeClass, { useIsDesktop } from 'hooks/use-size-class';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import VideoCard from 'components/video-card';
import { useEffect, useState, useRef } from 'react';
import useWindowWidth from 'hooks/use-window-width';
import { useTranslation } from 'next-i18next';
import { VideoCardData } from '@tenants';

const DEFAULT_CARD_WIDTH_DESKTOP = 450; // FIXME: Should be set by theme config
const DEFAULT_CARD_WIDTH_MOBILE = 400; // FIXME: Should be set by theme config

export type VideoCardCarouselProps = {
  videoCards: readonly VideoCardData[];
  style?: {
    cardWidth?: number | undefined;
  };
};

export default function VideoCardCarousel({ videoCards, style }: VideoCardCarouselProps) {
  const { t } = useTranslation();
  const isMobile = useSizeClass({ max: 675 });
  const isDesktop = useIsDesktop();
  const windowWidth = useWindowWidth();
  const cardWidth = style?.cardWidth ?? (isDesktop ? DEFAULT_CARD_WIDTH_DESKTOP : DEFAULT_CARD_WIDTH_MOBILE);



  const getPerView = () => {
    return Math.max(1.3, (windowWidth / cardWidth) + 0.2);
  };

  // On desktop, the active card is the one the user clicks on
  const [activeIndexDesktop, setActiveIndexDesktop] = useState(null);
  // On mobile, the active card is the one currently in view
  const [activeIndexMobile, setActiveIndexMobile] = useState(0);
  const [fullScreenIndex, setFullScreenIndex] = useState(null);
  const isFullScreen = fullScreenIndex != null;
  const componentRef = useRef(null);

  // Add support for scroll wheel
  const WheelControls = slider => {
    let touchTimeout;
    let position;
    let wheelActive;

    function dispatch(e, name) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        position.x -= e.deltaX;
      }
      slider.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        })
      );
    }

    const wheelStart = e => {
      position = {
        x: e.pageX,
        y: e.pageY,
      };
      dispatch(e, 'ksDragStart');
    };

    const wheel = e => {
      dispatch(e, 'ksDrag');
    };

    const wheelEnd = e => {
      dispatch(e, 'ksDragEnd');
    };

    const eventWheel = e => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.deltaX < 0) {
        setFullScreenIndex(null);
        e.preventDefault();
        if (!wheelActive) {
          wheelStart(e);
          wheelActive = true;
        }
        wheel(e);
        clearTimeout(touchTimeout);
        touchTimeout = setTimeout(() => {
          wheelActive = false;
          wheelEnd(e);
        }, 50);
      }
    };

    slider.on('created', () => {
      slider.container.addEventListener('wheel', eventWheel, {
        passive: false,
      });
    });
  };

  const [scrollerContainerRef, slider] = useKeenSlider({
    loop: true,
    mode: isFullScreen ? 'snap' : 'free',
    renderMode: 'performance',
    slides: {
      perView: getPerView(),
      spacing: isMobile ? isFullScreen ? 40 : 10 : 0,
      origin: 'center',
    },
    slideChanged: slider => {
      if (slider.track.details.rel !== activeIndexMobile) {
        exitFullScreen();
      }
      setActiveIndexMobile(slider.track.details.rel);
    },
  }, [WheelControls]);

  const getCardType = (index: number) => {
    if (index % 3 === 0) return 'primary';
    if (index % 3 === 1) return 'secondary';
    if (index % 3 === 2) return 'tertiary';
    return 'primary';
  };

  const exitFullScreen = () => {
    if (fullScreenIndex != null) {
      setFullScreenIndex(null);
    }
    window.removeEventListener('scroll', exitFullScreen);
  };

  /*const handleTouchStart = e => {
    setTouchStartX(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
    setTouchEndX(null);
    setTouchEndY(null);
  };

  const handleTouchMove = e => {
    setTouchEndX(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = e => {
    setTouchEndX(e.changedTouches[0].clientX);
    setTouchEndY(e.changedTouches[0].clientY);
    // Exit full screen if user swipes horizontally or vertically
    if ((touchEndY != null && touchEndX != null) &&
      (Math.abs(touchEndX - touchStartX) > minSwipeDistance || Math.abs(touchEndY - touchStartY) > minSwipeDistance)) {
      exitFullScreen();
    }
  };*/

  useEffect(() => {
    const scrollToSlide = async () => {
      slider.current?.moveToIdx(fullScreenIndex);
      await componentRef.current.scrollIntoView(true, { behavior: 'smooth', block: 'center' });
      // Add listener to exit full screen when user scrolls, have to add after scrollIntoView so that this scroll doesn't trigger the exit
      setTimeout(() => {
        window.addEventListener('scroll', exitFullScreen);
      }, 100);
      setFullScreenIndex(fullScreenIndex);
    };

    if (fullScreenIndex != null) {
      scrollToSlide();
    }

    return () => {
      window.removeEventListener('scroll', exitFullScreen);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreenIndex, slider]);

  return (
    <div className={buildClassName(styles.component, isFullScreen && styles['full-screen'])} ref={componentRef}>
      <div className={buildClassName(styles['scroller-outer'], isFullScreen && styles['full-screen-outer'])} id="scroller-outer">
        <div id="scroller" className={buildClassName(styles['scroller-inner'], 'keen-slider', isFullScreen && styles['full-screen-inner'])} ref={scrollerContainerRef}>
          {videoCards.map((videoCard, index) => {
            return (
              <div key={index} className={buildClassName('keen-slider__slide', styles['card-wrapper'])}>
                <VideoCard index={index} videoId={videoCard.videoId} bottomText={t(videoCard.bottomText)} cardType={getCardType(index)} isActiveDesktop={activeIndexDesktop === index} setActiveDesktop={setActiveIndexDesktop} isActiveMobile={activeIndexMobile === index} isMobileFullScreen={index === fullScreenIndex} setFullScreenIndex={setFullScreenIndex} shouldTranslate={!isFullScreen} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
